import type { I18n, MessageDescriptor } from '@lingui/core';

import {
  isEffect,
  screenPermissionAllows,
  type Permission,
  type ScreenManifest,
  type ScreenRegistry,
} from '@/shared/screen';

export type CommandGroup = 'goto' | 'screen' | 'preferences';

export interface Command {
  readonly id: string;
  readonly group: CommandGroup;
  readonly label: string;
  readonly hint?: string;
  readonly icon?: string;
  readonly keywords: readonly string[];
  readonly shortcut?: string;
  /** Abre fora do SPA (tela ainda no legado, U15). */
  readonly external?: boolean;
  /** A tela do destino pode ser fixada na Início: estrela no item e ⌘D com ele selecionado. */
  readonly pin?: CommandPin;
  readonly run: () => unknown;
}

export interface CommandPin {
  readonly pinned: boolean;
  readonly toggle: () => void;
}

/** Quem fixa telas na Início (a casca): o ⌘K só pergunta e repassa. */
export interface PinSupport {
  readonly canPin: (screen: ScreenManifest) => boolean;
  readonly isPinned: (screenId: string) => boolean;
  readonly toggle: (screenId: string) => void;
}

/** Destino de navegação já resolvido pela casca (permissão, tenant e legado aplicados). */
export interface LinkCommand {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly icon?: string;
  readonly href: string;
  readonly external: boolean;
}

export interface ExtraCommand {
  readonly id: string;
  readonly label: MessageDescriptor;
  readonly icon?: string;
  readonly keywords?: readonly MessageDescriptor[];
  readonly run: () => unknown;
}

export function buildCommands(deps: {
  registry: ScreenRegistry;
  activeScreen: ScreenManifest | undefined;
  can: (permission: Permission | undefined) => boolean;
  canAccess?: (screen: ScreenManifest) => boolean;
  i18n: I18n;
  go: (path: string) => unknown;
  preferences: readonly ExtraCommand[];
  links?: readonly LinkCommand[];
  open?: (url: string) => unknown;
  pin?: PinSupport;
}): Command[] {
  const { i18n } = deps;
  const out: Command[] = [];
  const accessible = (screen: ScreenManifest) =>
    screenPermissionAllows(screen, deps.can) && (deps.canAccess?.(screen) ?? true);
  const pinOf = (screen: ScreenManifest | undefined): { pin?: CommandPin } => {
    const support = deps.pin;
    if (!support || !screen || !support.canPin(screen)) return {};
    return { pin: { pinned: support.isPinned(screen.id), toggle: () => support.toggle(screen.id) } };
  };
  const screens = deps.registry.screens
    .filter((s) => s.nav && accessible(s))
    .sort((a, b) => (a.nav?.order ?? 0) - (b.nav?.order ?? 0));
  for (const s of screens) {
    out.push({
      id: `goto:${s.id}`,
      group: 'goto',
      label: i18n._(s.title),
      ...(s.nav ? { hint: i18n._(s.nav.group), icon: s.nav.icon } : {}),
      keywords: [s.id],
      ...pinOf(s),
      run: () => deps.go(s.path),
    });
  }
  const seen = new Set(out.map((c) => c.id));
  for (const l of deps.links ?? []) {
    const linkedScreen = deps.registry.byPath(l.href);
    if (linkedScreen && !accessible(linkedScreen)) continue;
    if (!l.external && screens.some((s) => s.path === l.href)) continue;
    const id = `goto:${l.id}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      group: 'goto',
      label: l.label,
      ...(l.hint ? { hint: l.hint } : {}),
      ...(l.icon ? { icon: l.icon } : {}),
      keywords: [],
      external: l.external,
      ...(l.external ? {} : pinOf(linkedScreen)),
      run: () =>
        l.external
          ? (deps.open ?? ((url: string) => window.location.assign(url)))(l.href)
          : deps.go(l.href),
    });
  }
  const active = deps.activeScreen;
  if (active && accessible(active)) {
    for (const a of active.actions) {
      if (a.row || isEffect(a) || a.input || !deps.can(a.permission)) continue;
      out.push({
        id: `screen:${a.id}`,
        group: 'screen',
        label: i18n._(a.label),
        keywords: (a.keywords ?? []).map((k) => i18n._(k)),
        ...(a.shortcut ? { shortcut: a.shortcut } : {}),
        run: () =>
          a.run({ input: {}, nav: { to: (path) => void deps.go(path), search: () => undefined } }),
      });
    }
  }
  for (const p of deps.preferences) {
    out.push({
      id: `pref:${p.id}`,
      group: 'preferences',
      label: i18n._(p.label),
      ...(p.icon ? { icon: p.icon } : {}),
      keywords: (p.keywords ?? []).map((k) => i18n._(k)),
      run: p.run,
    });
  }
  return out;
}
